import { useState, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsCheckCircle } from 'react-icons/bs';
import { HiViewColumns } from 'react-icons/hi2';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Select from '#globalComponents/Forms/Select';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useErrorStore } from '#lib/stores/errorStore';

import { useCreateDealsFromCompanies } from '../../../models/imports/mutations';
import { usePipelines } from '../../../models/pipelines/queries';

// Entity wording per source. `verb` already includes the German singular/plural verb form.
const ENTITY_LABELS = {
	crmCompanies: { singular: 'Firma', plural: 'Firmen', verbSingular: 'Firma wird', verbPlural: 'Firmen werden' },
	leads: { singular: 'Lead', plural: 'Leads', verbSingular: 'Lead wird', verbPlural: 'Leads werden' },
};

/** Result screen shown after the import completes: created / skipped / failed counts. */
const ImportSummary = ({ summary, labels }) => (
	<>
		<div className="grid grid-cols-3 gap-3 mb-4">
			<div className="bg-success-200 rounded-card p-3 text-center">
				<div className="text-lg font-bold text-success-700">{summary.created}</div>
				<div className="text-xs text-success-700">Angelegt</div>
			</div>
			<div className="bg-caution-200 rounded-card p-3 text-center">
				<div className="text-lg font-bold text-caution-700">{summary.skipped}</div>
				<div className="text-xs text-caution-700">Übersprungen</div>
			</div>
			<div className={`rounded-card p-3 text-center ${summary.failed > 0 ? 'bg-warning-200' : 'bg-bgBase-300'}`}>
				<div className={`text-lg font-bold ${summary.failed > 0 ? 'text-warning-700' : 'text-bgBase-800'}`}>
					{summary.failed}
				</div>
				<div className={`text-xs ${summary.failed > 0 ? 'text-warning-700' : 'text-bgBase-800'}`}>
					Fehlgeschlagen
				</div>
			</div>
		</div>

		{summary.failed > 0 && (
			<Alert tone="warning" className="p-3">
				Einige {labels.plural} konnten nicht importiert werden. Details finden Sie in den Server-Logs.
			</Alert>
		)}
	</>
);

ImportSummary.propTypes = {
	summary: PropTypes.shape({
		requested: PropTypes.number,
		created: PropTypes.number,
		skipped: PropTypes.number,
		failed: PropTypes.number,
	}).isRequired,
	labels: PropTypes.shape({ singular: PropTypes.string, plural: PropTypes.string }).isRequired,
};

/**
 * Opened from a selection bar. Takes either a table selection (`tableScope`: lead companies of a
 * search query — auto-imported to the CRM by the backend — or companies of a contact list) or ids
 * of companies that already exist in the CRM (crmCompanyIds), and lets the user pick pipeline +
 * stage. The import result (created / skipped / failed) is shown on the "done" screen.
 *
 * Idempotent: backend dedups against open deals by default (skipDuplicates=true).
 */

const TransferLeadsToCrmModal = ({
	isOpen,
	onClose,
	tableScope = null,
	count: scopeCount = 0,
	crmCompanyIds = null,
}) => {
	const fromCrmCompanies = Array.isArray(crmCompanyIds) && crmCompanyIds.length > 0;
	const count = fromCrmCompanies ? crmCompanyIds.length : scopeCount;
	const labels = tableScope?.table === 'leads' ? ENTITY_LABELS.leads : ENTITY_LABELS.crmCompanies;
	const [pipelineId, setPipelineId] = useState('');
	const [stageId, setStageId] = useState('');
	const [backfillEmails, setBackfillEmails] = useState(true);
	const [skipDuplicates, setSkipDuplicates] = useState(true);
	const [summary, setSummary] = useState(null);

	const { data: pipelines = [], isLoading: pipelinesLoading } = usePipelines();
	const importMutation = useCreateDealsFromCompanies();
	const showError = useErrorStore((s) => s.showError);
	const showSuccess = useErrorStore((s) => s.showSuccess);

	// Preselect default pipeline + its first stage when the modal opens
	useEffect(() => {
		if (!isOpen) return;
		setSummary(null);
		if (pipelines.length > 0 && !pipelineId) {
			const def = pipelines.find((p) => p.isDefault) ?? pipelines[0];
			setPipelineId(def.id);
		}
	}, [isOpen, pipelines, pipelineId]);

	const selectedPipeline = useMemo(() => pipelines.find((p) => p.id === pipelineId), [pipelines, pipelineId]);
	const stages = useMemo(
		() => (selectedPipeline?.stages ?? []).slice().sort((a, b) => a.stageOrder - b.stageOrder),
		[selectedPipeline],
	);

	// Auto-pick first stage of the selected pipeline
	useEffect(() => {
		if (stages.length > 0 && !stages.find((s) => s.id === stageId)) {
			setStageId(stages[0].id);
		}
	}, [stages, stageId]);

	const handleClose = () => {
		setSummary(null);
		setPipelineId('');
		setStageId('');
		onClose();
	};

	const handleSubmit = async () => {
		if (!pipelineId || !stageId) return;
		try {
			const result = await importMutation.mutateAsync({
				...(fromCrmCompanies ? { crmCompanyIds } : { scope: tableScope }),
				pipelineId,
				stageId,
				backfillEmails,
				skipDuplicates,
			});
			setSummary(result.summary);
			if (result.summary?.created > 0) {
				showSuccess({
					title: 'Zum CRM hinzugefügt',
					message: `${result.summary.created} Deal${result.summary.created === 1 ? '' : 's'} erfolgreich angelegt.`,
				});
			}
		} catch (err) {
			showError({ error: err, title: 'Übertragung ins CRM fehlgeschlagen' });
		}
	};

	if (!isOpen) return null;

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="m">
			{summary ? (
				<Panel.Header
					icon={BsCheckCircle}
					onClose={handleClose}
					subtitle={`${summary.requested} ${summary.requested === 1 ? labels.singular : labels.plural} verarbeitet.`}
				>
					Übertragung abgeschlossen
				</Panel.Header>
			) : (
				<Panel.Header
					icon={HiViewColumns}
					onClose={handleClose}
					subtitle={`${count} ${count === 1 ? labels.verbSingular : labels.verbPlural} als Deal angelegt.`}
				>
					Ins CRM übertragen
				</Panel.Header>
			)}
			{summary ? (
				<Panel.Body>
					<ImportSummary summary={summary} labels={labels} />
				</Panel.Body>
			) : (
				<Panel.Body>
					{pipelinesLoading && <p className="text-bgBase-800 text-sm">Pipelines werden geladen …</p>}

					{!pipelinesLoading && pipelines.length === 0 && (
						<Alert tone="caution">
							Sie haben noch keine Pipeline angelegt. Öffnen Sie zuerst das CRM und legen Sie eine
							Pipeline an.
						</Alert>
					)}

					{!pipelinesLoading && pipelines.length > 0 && (
						<div className="space-y-4">
							<div>
								<label className="block text-xs font-semibold uppercase tracking-wide text-bgBase-800 mb-1">
									Pipeline
								</label>
								<Select value={pipelineId} onChange={(e) => setPipelineId(e.target.value)}>
									{pipelines.map((p) => (
										<option key={p.id} value={p.id}>
											{p.name}
											{p.isDefault ? ' (Standard)' : ''}
										</option>
									))}
								</Select>
							</div>

							<div>
								<label className="block text-xs font-semibold uppercase tracking-wide text-bgBase-800 mb-1">
									Stage
								</label>
								<Select
									value={stageId}
									onChange={(e) => setStageId(e.target.value)}
									disabled={stages.length === 0}
								>
									{stages.map((s) => (
										<option key={s.id} value={s.id}>
											{s.name}
										</option>
									))}
								</Select>
							</div>

							<div className="space-y-2 pt-2 border-t border-bgBase-400">
								<Checkbox
									id="transfer-leads-backfill-emails"
									checked={backfillEmails}
									onChange={setBackfillEmails}
									label="Bestehende E-Mail-Versands als Aktivitäten übernehmen"
								/>
								<Checkbox
									id="transfer-leads-skip-duplicates"
									checked={skipDuplicates}
									onChange={setSkipDuplicates}
									label={`${labels.plural} überspringen, die bereits einen offenen Deal haben`}
								/>
							</div>
						</div>
					)}
				</Panel.Body>
			)}
			{summary ? (
				<Panel.Footer className="justify-end">
					<Button variant="solid" onClick={handleClose}>
						Schließen
					</Button>
				</Panel.Footer>
			) : (
				<Panel.Footer className="justify-end">
					<Button variant="outline" onClick={handleClose}>
						Abbrechen
					</Button>
					<Button
						variant="solid"
						onClick={handleSubmit}
						disabled={!pipelineId || !stageId || count === 0 || pipelinesLoading || pipelines.length === 0}
						loading={importMutation.isPending}
					>
						{count === 1 ? 'Deal anlegen' : `${count} Deals anlegen`}
					</Button>
				</Panel.Footer>
			)}
		</Modal>
	);
};

TransferLeadsToCrmModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	// Table selection to transfer, used when crmCompanyIds is not set: lead companies of a search
	// query ({ table: 'leads', contextId: prospectQueryId, … }) or companies of a contact list
	// ({ table: 'contactListCompanies', contextId: listId, … }); ids XOR selectAll + filters.
	tableScope: PropTypes.shape({
		table: PropTypes.oneOf(['leads', 'crmCompanies', 'contactListCompanies']).isRequired,
		contextId: PropTypes.string,
		ids: PropTypes.arrayOf(PropTypes.string),
		selectAll: PropTypes.bool,
		filters: PropTypes.object,
	}),
	// Number of companies tableScope covers.
	count: PropTypes.number,
	// Ids of companies that already exist in the CRM (e.g. contact-list recipients).
	crmCompanyIds: PropTypes.arrayOf(PropTypes.string),
};

export default TransferLeadsToCrmModal;
